import { Add, Delete } from '@mui/icons-material';
import {
  Autocomplete,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getCostCenters } from '../../../../../../../../lib/api/accounting';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import { formatMoney } from '../../../../../../../../lib/functions/utils';
import {
  AccountingEntryLine,
  AccountingNode,
  CostCenter,
  EditEntryWithLinesProps,
  EntryWithLinesHandleChangeEvent,
} from '../../../../types';

const EditEntryWithLines = (props: EditEntryWithLinesProps) => {
  const { entry, setEntry, nodes, free } = props;
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [nodeOptions, setNodeOptions] = useState<string[]>([]);

  const dispatch = useDispatch();

  const handleChange = (
    e: EntryWithLinesHandleChangeEvent,
    lineIndex: number,
    attribute: string,
  ) => {
    let value = e.target.value;
    if (value === null || value === undefined) return;

    // Convert to string for processing
    const stringValue = String(value);

    if (attribute === 'debit' || attribute === 'credit') {
      value =
        parseInt(stringValue.split('.').join('').split('$').join('')) || 0;
    }
    const newEntry = { ...entry };
    newEntry.lines = newEntry.lines.map(
      (line: AccountingEntryLine, index: number) => {
        if (index === lineIndex) {
          return { ...line, [attribute]: value };
        }
        return line;
      },
    );
    setEntry(newEntry);
  };

  const handleDeleteLine = (lineIndex: number) => {
    const newEntry = { ...entry };
    newEntry.lines = newEntry.lines.filter(
      (line: AccountingEntryLine, index: number) => index !== lineIndex,
    );
    setEntry(newEntry);
  };

  const handleAddLine = () => {
    const newLine = {
      nodeId: null,
      debit: 0,
      credit: 0,
      costCenterId: null,
      documentKey: '',
      businessIdentifier: entry?.lines?.[0]?.businessIdentifier || null,
    };
    setEntry({ ...entry, lines: [...entry.lines, newLine] });
  };

  const totalSumEquals = entry.lines.reduce(
    (acc: number, line: AccountingEntryLine) => {
      return acc + line.debit - line.credit;
    },
    0,
  );

  const fetchCostCenters = async () => {
    try {
      const response = await getCostCenters(dispatch);
      setCostCenters(response);
    } catch (error) {
      setCostCenters([]);
    }
  };

  useEffect(() => {
    fetchCostCenters().catch(() => setCostCenters([]));
  }, []);
  useEffect(() => {
    const options = new Set<string>();
    nodes.forEach((node: AccountingNode) => {
      options.add(node.key);
    });
    setNodeOptions(Array.from(options));
  }, [nodes]);

  const getSelectedNode = (lineIndex: number) => {
    const line = entry.lines[lineIndex];
    if (line.node) {
      return line.node;
    }
    return nodes.find((node: AccountingNode) => node.id === line.nodeId);
  };

  return (
    <div style={{ padding: '20px 0px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        {free ? (
          <TextField
            label="Glosa asiento"
            value={entry.name}
            onChange={(e) => setEntry({ ...entry, name: e.target.value })}
          />
        ) : (
          <Typography variant="h6">{entry.name}</Typography>
        )}
        <div>
          <CustomButton onClick={handleAddLine}>
            <Add />
          </CustomButton>
        </div>
      </div>

      <div style={{ padding: '10px 0px' }}>
        {entry.lines.map((line: AccountingEntryLine, index: number) => {
          const selectedNode = getSelectedNode(index);
          return (
            <Grid
              container
              spacing={2}
              style={{ padding: '4px 0px' }}
              key={line.id || index}
            >
              <Grid item xs={2}>
                <Autocomplete
                  fullWidth
                  options={nodeOptions}
                  getOptionLabel={(option) => option || ''}
                  value={selectedNode?.key || null}
                  onChange={(e, newValue) => {
                    const selectedNode = nodes.find(
                      (node: AccountingNode) => node.key === newValue,
                    );
                    handleChange(
                      {
                        target: {
                          value: selectedNode ? selectedNode.id : null,
                        },
                      },
                      index,
                      'nodeId',
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cuenta"
                      placeholder="Buscar cuenta..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1.5}>
                <TextField
                  label="Debe"
                  fullWidth
                  style={{ textAlign: 'right' }}
                  value={formatMoney(line.debit)}
                  onChange={(e) => handleChange(e, index, 'debit')}
                />
              </Grid>
              <Grid item xs={1.5}>
                <TextField
                  style={{ textAlign: 'right' }}
                  label="Haber"
                  value={formatMoney(line.credit)}
                  onChange={(e) => handleChange(e, index, 'credit')}
                />
              </Grid>
              <Grid item xs={2}>
                {selectedNode?.costCenterRequired ? (
                  <Autocomplete
                    fullWidth
                    options={costCenters}
                    getOptionLabel={(costCenter: CostCenter) => costCenter.name}
                    value={
                      costCenters.find(
                        (cc: CostCenter) => cc.id === line.costCenterId,
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      handleChange(
                        {
                          target: { value: newValue ? newValue.id : null },
                        },
                        index,
                        'costCenterId',
                      )
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Centro de Costo" required />
                    )}
                  />
                ) : (
                  <div style={{ height: '56px' }} />
                )}
              </Grid>
              <Grid item xs={2}>
                {selectedNode?.documentKeyRequired ? (
                  <FormControl fullWidth>
                    <InputLabel>Tipo Doc.</InputLabel>
                    <Select
                      value={line.documentKey || ''}
                      onChange={(e) => handleChange(e, index, 'documentKey')}
                      label="Tipo Doc."
                      required
                    >
                      {/* TODO: Add other document types */}
                      <MenuItem value="electronic-invoice">Factura</MenuItem>
                      <MenuItem value="otro">Otro</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <div style={{ height: '56px' }} />
                )}
              </Grid>
              <Grid item xs={2}>
                {selectedNode?.businessIdentifierRequired ? (
                  <TextField
                    fullWidth
                    label="Auxiliar (RUT)"
                    value={line.businessIdentifier || ''}
                    onChange={(e) =>
                      handleChange(e, index, 'businessIdentifier')
                    }
                    required
                  />
                ) : (
                  <div style={{ height: '56px' }} />
                )}
              </Grid>
              <Grid item xs={1}>
                <IconButton
                  onClick={() => handleDeleteLine(index)}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          );
        })}
        <Divider />
        <Grid container spacing={2} style={{ padding: '4px 0px' }}>
          <Grid item xs={5}>
            <Typography fontWeight="bold">Totales</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              fontWeight="bold"
              color={totalSumEquals === 0 ? 'black' : 'red'}
              variant="body1"
              style={{ textAlign: 'right' }}
            >
              {formatMoney(
                entry.lines.reduce(
                  (acc: number, line: AccountingEntryLine) => acc + line.debit,
                  0,
                ),
              )}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              fontWeight="bold"
              color={totalSumEquals === 0 ? 'black' : 'red'}
              variant="body1"
              style={{ textAlign: 'right' }}
            >
              {formatMoney(
                entry.lines.reduce(
                  (acc: number, line: AccountingEntryLine) => acc + line.credit,
                  0,
                ),
              )}
            </Typography>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default EditEntryWithLines;
